import React, { useContext } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { ThemeContext } from 'styled-components';

import tracker_2G from 'assets/images/tracker-2G.png';
import tracker_4G from 'assets/images/tracker-4G.png';
import tracker_plug_and_play from 'assets/images/tracker_plug_and_play.png';

import {
  ActionPortal,
  ButtonWithIcon,
  Checkbox,
  DefaultActionButtons,
  Form,
  Icon,
  MaskedInput,
  Step,
  Text,
} from 'components';

import { Gray, Purple, Orange } from 'constants/colors';
import { StepPropTypes } from 'constants/propTypes';

const STEPS = {
  CAMERAS_MODELS_SELECTION: 3,
  BLOCKER: 4,
};

const formValidationSchema = yup.object().shape({
  tracker4GCount: yup.number().integer().min(0),
  tracker2GCount: yup.number().integer().min(0),
  trackerPlugAndPlayCount: yup.number().integer().min(0),
  acceptTerms: yup.boolean().oneOf([true], 'Você deve aceitar os termos'),
});

export default function QuantityTypes(props) {
  const theme = useContext(ThemeContext);
  const { onSubmit, onCancel, initialValues: defaultValues } = props;

  const {
    control,
    formState,
    handleSubmit,
    watch,
    setValue,
    trigger,
    register,
  } = useForm({
    mode: 'all',
    defaultValues: {
      tracker4GCount: defaultValues?.quantityTypes?.tracker4GCount || 0,
      tracker2GCount: defaultValues?.quantityTypes?.tracker2GCount || 0,
      trackerPlugAndPlayCount: defaultValues?.quantityTypes?.trackerPlugAndPlayCount || 0,
      acceptTerms: defaultValues?.quantityTypes?.acceptTerms || false,
    },
    resolver: yupResolver(formValidationSchema),
  });

  const tracker4GCount = watch('tracker4GCount');
  const tracker2GCount = watch('tracker2GCount');
  const trackerPlugAndPlayCount = watch('trackerPlugAndPlayCount');

  const { isValid } = formState;

  const submitForm = (e) => {
    e.preventDefault();
    handleSubmit(onSubmit)();
  };

  const totalContractedTrackers = defaultValues?.vehicleManager?.conteleTrackerCount;
  const totalSelectedTrackers = (Number(tracker2GCount) || 0) + (Number(tracker4GCount) || 0) + (Number(trackerPlugAndPlayCount) || 0);
  const isQuantityValid = totalSelectedTrackers === totalContractedTrackers;
  const canAddMore = totalSelectedTrackers < totalContractedTrackers;
  const isFormValid = isValid && isQuantityValid && watch('acceptTerms');

  const calculateNextStepPosition = (values) => {
    const conteleCamerasCount = defaultValues?.vehicleManager?.conteleCamerasCount || 0;
    if (conteleCamerasCount > 0) {
      return STEPS.CAMERAS_MODELS_SELECTION;
    }
    return STEPS.BLOCKER;
  };

  const baseFeatures = [
    { label: 'Envia dados de 15 em 15 segundos', isAvailable: true },
    { label: 'Homologado pela Anatel', isAvailable: true },
    { label: 'Relê de Bloqueio opcional', isAvailable: true },
    { label: 'Armazena eventos em Área de Sombra', isAvailable: true },
    { label: 'Registra: Aceleração, Curva e Freada Brusca', isAvailable: true },
    { label: 'Chip multioperadora', isAvailable: true },
    { label: 'Instalação com apenas 2 fios', isAvailable: false },
    { label: 'Cobertura mais abrangente 2G + 4G', isAvailable: false },
  ];

  const getTrackerFeatures = (type) => baseFeatures.map((feature) => {
    if (type === 'plugAndPlay') {
      if (feature.label === 'Relê de Bloqueio opcional') {
        return { ...feature, isAvailable: false };
      }
      if (feature.label === 'Cobertura mais abrangente 2G + 4G') {
        return { ...feature, isAvailable: true };
      }
    }
    return feature;
  });

  return (
    <div style={{ height: '100%', backgroundColor: Gray[200] }}>
      <Step
        headerTitle="Escolha a quantidade de cada modelo dos rastreadores"
        style={{
          backgroundColor: Gray[200],
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Form onKeyPress={(e) => e.key === 'Enter' && submitForm(e)}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '1100px',
              border: `1px solid ${Gray[250]}`,
              gap: '8px',
              borderRadius: '5px',
              backgroundColor: Gray[100],
              padding: '20px',
              minHeight: '600px',
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: '20px',
                justifyContent: 'space-between',
              }}
            >
              <section
                style={{
                  width: '400px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <section
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={tracker_2G}
                    alt="2g"
                    style={{ width: '100%', objectFit: 'contain' }}
                  />
                  <p style={{ fontSize: 18, fontWeight: 500 }}>
                    Rastreador 2G
                    {' '}
                    <span
                      style={{ fontSize: 14, fontWeight: 400, opacity: 0.6 }}
                    >
                      (com fio)
                    </span>
                  </p>
                  <div
                    style={{
                      backgroundColor: theme.newPalette.background.success[200],
                      color: theme.newPalette.background.success[100],
                      borderRadius: 4,
                      padding: '2px 10px',
                      fontWeight: 500,
                      margin: '10px',
                    }}
                  >
                    Em média 35% off
                  </div>
                  <div
                    style={{
                      width: '100%',
                      height: 1,
                      backgroundColor: theme.newPalette.background.gray[300],
                      marginBottom: '20px',
                      marginTop: '12px',
                    }}
                  />
                </section>
                <article
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    padding: '0px 32px',
                    gap: '10px',
                  }}
                >
                  {getTrackerFeatures('2G').map((item) => (
                    <p
                      style={{
                        display: 'flex',
                        gap: 5,
                        alignItems: 'center',
                        textDecoration: item.isAvailable
                          ? 'none'
                          : 'line-through',
                        fontSize: 14,
                      }}
                    >
                      <CheckIcon value={item.isAvailable} />
                      <span>{item.label}</span>
                    </p>
                  ))}
                </article>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    marginTop: 32,
                  }}
                >
                  <Text
                    color={Gray[500]}
                    fontSize="16px"
                    lineHeight="19px"
                    fontWeight="600"
                  >
                    Quantidade de veículos com Rastreador
                  </Text>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <ButtonWithIcon
                      icon="minus"
                      customBackgroundColor={Gray[100]}
                      border={`1px solid ${Gray[250]}`}
                      customIconColor={Gray[400]}
                      height="32px"
                      iconOptions={{ width: '14px', height: '14px' }}
                      onClick={() => {
                        if ((Number(tracker2GCount) || 0) === 0) {
                          return;
                        }
                        setValue('tracker2GCount', (Number(tracker2GCount) || 1) - 1);
                        trigger();
                      }}
                    />
                    <MaskedInput
                      control={control}
                      name="tracker2GCount"
                      placeholder="0"
                      format="numeric"
                      containerWidth="100%"
                      style={{
                        border: `1px solid ${Gray[250]}`,
                        borderRadius: '5px',
                      }}
                    />
                    <ButtonWithIcon
                      icon="plus"
                      customBackgroundColor={Gray[100]}
                      border={`1px solid ${Gray[250]}`}
                      customIconColor={Gray[400]}
                      height="32px"
                      iconOptions={{ width: '14px', height: '14px' }}
                      onClick={() => {
                        if (!canAddMore) {
                          return;
                        }
                        setValue('tracker2GCount', (Number(tracker2GCount) || 0) + 1);
                        trigger();
                      }}
                      disabled={!canAddMore}
                    />
                  </div>
                </div>
              </section>

              <section
                style={{
                  width: '400px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <section
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={tracker_4G}
                    alt="2g"
                    style={{ width: '100%', objectFit: 'contain' }}
                  />
                  <p style={{ fontSize: 18, fontWeight: 500 }}>
                    Rastreador 4G
                    {' '}
                    <span
                      style={{ fontSize: 14, fontWeight: 400, opacity: 0.6 }}
                    >
                      (com fio)
                    </span>
                  </p>
                  <div
                    style={{
                      height: '32px',
                      margin: '10px',
                    }}
                  />
                  <div
                    style={{
                      width: '100%',
                      height: 1,
                      backgroundColor: theme.newPalette.background.gray[300],
                      marginBottom: '20px',
                      marginTop: '12px',
                    }}
                  />
                </section>
                <article
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    padding: '0px 32px',
                    gap: '10px',
                  }}
                >
                  {getTrackerFeatures('4G').map((item) => (
                    <p
                      style={{
                        display: 'flex',
                        gap: 5,
                        alignItems: 'center',
                        fontSize: 14,
                      }}
                    >
                      <CheckIcon value />
                      <span>{item.label}</span>
                    </p>
                  ))}
                </article>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    marginTop: 32,
                  }}
                >
                  <Text
                    color={Gray[500]}
                    fontSize="16px"
                    lineHeight="19px"
                    fontWeight="600"
                  >
                    Quantidade de veículos com Rastreador
                  </Text>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <ButtonWithIcon
                      icon="minus"
                      customBackgroundColor={Gray[100]}
                      border={`1px solid ${Gray[250]}`}
                      customIconColor={Gray[400]}
                      height="32px"
                      iconOptions={{ width: '14px', height: '14px' }}
                      onClick={() => {
                        if ((Number(tracker4GCount) || 0) === 0) {
                          return;
                        }
                        setValue('tracker4GCount', (Number(tracker4GCount) || 1) - 1);
                        trigger();
                      }}
                    />
                    <MaskedInput
                      control={control}
                      name="tracker4GCount"
                      placeholder="0"
                      format="numeric"
                      containerWidth="100%"
                      style={{
                        border: `1px solid ${Gray[250]}`,
                        borderRadius: '5px',
                      }}
                      maxLength="3"
                      min={0}
                    />
                    <ButtonWithIcon
                      icon="plus"
                      customBackgroundColor={Gray[100]}
                      border={`1px solid ${Gray[250]}`}
                      customIconColor={Gray[400]}
                      height="32px"
                      iconOptions={{ width: '14px', height: '14px' }}
                      onClick={() => {
                        if (!canAddMore) {
                          return;
                        }
                        setValue('tracker4GCount', (Number(tracker4GCount) || 0) + 1);
                        trigger();
                      }}
                      disabled={!canAddMore}
                    />
                  </div>
                </div>
              </section>

              <section
                style={{
                  width: '400px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <section
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={tracker_plug_and_play}
                    alt="plug and play"
                    style={{ width: '100%', objectFit: 'contain' }}
                  />
                  <p style={{ fontSize: 18, fontWeight: 500 }}>
                    Plug and Play 4G
                    {' '}
                    <span
                      style={{ fontSize: 14, fontWeight: 400, opacity: 0.6 }}
                    >
                      (sem fio)
                    </span>
                  </p>
                  <div
                    style={{
                      height: '32px',
                      margin: '10px',
                    }}
                  />
                  <div
                    style={{
                      width: '100%',
                      height: 1,
                      backgroundColor: theme.newPalette.background.gray[300],
                      marginBottom: '20px',
                      marginTop: '12px',
                    }}
                  />
                </section>
                <article
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    padding: '0px 32px',
                    gap: '10px',
                  }}
                >
                  {getTrackerFeatures('plugAndPlay').map((item) => (
                    <p
                      style={{
                        display: 'flex',
                        gap: 5,
                        alignItems: 'center',
                        textDecoration: item.isAvailable
                          ? 'none'
                          : 'line-through',
                        fontSize: 14,
                      }}
                    >
                      <CheckIcon value={item.isAvailable} />
                      <span>{item.label}</span>
                    </p>
                  ))}
                </article>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    marginTop: 32,
                  }}
                >
                  <Text
                    color={Gray[500]}
                    fontSize="16px"
                    lineHeight="19px"
                    fontWeight="600"
                  >
                    Quantidade de veículos com Rastreador
                  </Text>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <ButtonWithIcon
                      icon="minus"
                      customBackgroundColor={Gray[100]}
                      border={`1px solid ${Gray[250]}`}
                      customIconColor={Gray[400]}
                      height="32px"
                      iconOptions={{ width: '14px', height: '14px' }}
                      onClick={() => {
                        if ((Number(trackerPlugAndPlayCount) || 0) === 0) {
                          return;
                        }
                        setValue('trackerPlugAndPlayCount', (Number(trackerPlugAndPlayCount) || 1) - 1);
                        trigger();
                      }}
                    />
                    <MaskedInput
                      control={control}
                      name="trackerPlugAndPlayCount"
                      placeholder="0"
                      format="numeric"
                      containerWidth="100%"
                      style={{
                        border: `1px solid ${Gray[250]}`,
                        borderRadius: '5px',
                      }}
                      maxLength="3"
                      min={0}
                    />
                    <ButtonWithIcon
                      icon="plus"
                      customBackgroundColor={Gray[100]}
                      border={`1px solid ${Gray[250]}`}
                      customIconColor={Gray[400]}
                      height="32px"
                      iconOptions={{ width: '14px', height: '14px' }}
                      onClick={() => {
                        if (!canAddMore) {
                          return;
                        }
                        setValue('trackerPlugAndPlayCount', (Number(trackerPlugAndPlayCount) || 0) + 1);
                        trigger();
                      }}
                      disabled={!canAddMore}
                    />
                  </div>
                </div>
              </section>
            </div>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              backgroundColor: Gray[200],
              borderRadius: '5px',
              padding: '20px',
            }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Icon
                  color={Gray[400]}
                  icon="exclamationCircle"
                  style={{ width: '16px', height: '16px' }}
                />
                <Text
                  color={Gray[400]}
                  fontSize="16px"
                  lineHeight="19px"
                  fontWeight="700"
                >
                  Instalação dos rastreadores
                </Text>
              </div>
              <Text
                color={Gray[400]}
                fontSize="14px"
                lineHeight="19px"
                fontWeight="400"
              >
                Você receberá o kit do equipamento já configurado com um manual de instalação. A responsabilidade pela instalação, retirada e/ou substituição é da empresa CONTRATANTE. A instalação precisa ser feita por um profissional especializado, sendo que todo o suporte necessário será fornecido pela Contele. Não há cobrança de instalação por nossa parte.
              </Text>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  type="checkbox"
                  control={control}
                  name="acceptTerms"
                  id="acceptTerms"
                  innerRef={register({
                    required: true,
                  })}
                />
                <Text
                  color={Gray[400]}
                  fontSize="14px"
                  lineHeight="19px"
                  fontWeight="400"
                  textAlign="center"
                >
                  Sim, estou ciente.
                </Text>
              </div>
            </div>

            {!isFormValid && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                backgroundColor: Orange[100],
                borderRadius: '5px',
                padding: '20px',
              }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <Icon
                    color={Orange[400]}
                    icon="exclamationTriangle"
                    style={{ width: '16px', height: '16px' }}
                  />
                  <Text
                    color={Orange[400]}
                    fontSize="16px"
                    lineHeight="19px"
                    fontWeight="700"
                  >
                    Requisitos para Prosseguir
                  </Text>
                </div>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                }}
                >
                  {!isQuantityValid && (
                    <Text
                      color={Orange[400]}
                      fontSize="14px"
                      lineHeight="19px"
                      fontWeight="400"
                    >
                      • Você precisa selecionar exatamente {totalContractedTrackers} rastreadores (atualmente selecionados: {totalSelectedTrackers})
                    </Text>
                  )}
                  {!watch('acceptTerms') && (
                    <Text
                      color={Orange[400]}
                      fontSize="14px"
                      lineHeight="19px"
                      fontWeight="400"
                    >
                      • É necessário aceitar os termos de instalação dos rastreadores
                    </Text>
                  )}
                </div>
              </div>
            )}
          </div>
          <ActionPortal>
            <DefaultActionButtons
              theme={theme}
              isValid={isFormValid}
              backButtonStyle={{
                iconPosition: 'left',
                border: 'none',
                customBackgroundColor: 'transparent',
                customIconColor: Gray[400],
                customTextColor: Gray[400],
                title: 'Passo Anterior',
              }}
              nextButtonStyle={{
                customBackgroundColor: Purple[500],
                customIconColor: Gray[100],
                customTextColor: Gray[100],
                title: 'Próximo Passo',
              }}
              onCancel={onCancel}
              onSubmit={() => handleSubmit((values) => onSubmit({
                ...values,
                nextStepIndexPosition: calculateNextStepPosition(values),
              }))()}
            />
          </ActionPortal>
        </Form>
      </Step>
    </div>
  );
}

export const CheckIcon = ({ value }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 14 15"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {value ? (
      <path
        d="M14 7.32886C14 11.1949 10.866 14.3289 7 14.3289C3.134 14.3289 0 11.1949 0 7.32886C0 3.46285 3.134 0.328857 7 0.328857C10.866 0.328857 14 3.46285 14 7.32886ZM6.19031 11.0353L11.3839 5.84175C11.5602 5.6654 11.5602 5.37944 11.3839 5.20309L10.7452 4.56442C10.5688 4.38804 10.2829 4.38804 10.1065 4.56442L5.87097 8.79993L3.8935 6.82246C3.71714 6.6461 3.43119 6.6461 3.2548 6.82246L2.61614 7.46112C2.43978 7.63748 2.43978 7.92343 2.61614 8.09979L5.55162 11.0353C5.728 11.2117 6.01393 11.2117 6.19031 11.0353Z"
        fill="#12B76A"
      />
    ) : (
      <path
        d="M7 0.328857C3.13306 0.328857 0 3.46192 0 7.32886C0 11.1958 3.13306 14.3289 7 14.3289C10.8669 14.3289 14 11.1958 14 7.32886C14 3.46192 10.8669 0.328857 7 0.328857ZM10.4323 9.16636C10.5649 9.29902 10.5649 9.51353 10.4323 9.6462L9.31452 10.7611C9.18185 10.8938 8.96734 10.8938 8.83468 10.7611L7 8.9095L5.1625 10.7611C5.02984 10.8938 4.81532 10.8938 4.68266 10.7611L3.56774 9.64337C3.43508 9.51071 3.43508 9.2962 3.56774 9.16353L5.41935 7.32886L3.56774 5.49136C3.43508 5.3587 3.43508 5.14418 3.56774 5.01152L4.68548 3.89378C4.81814 3.76112 5.03266 3.76112 5.16532 3.89378L7 5.74821L8.8375 3.8966C8.97016 3.76394 9.18468 3.76394 9.31734 3.8966L10.4351 5.01434C10.5677 5.147 10.5677 5.36152 10.4351 5.49418L8.58064 7.32886L10.4323 9.16636Z"
        fill="#B42318"
      />
    )}
  </svg>
);

QuantityTypes.propTypes = StepPropTypes;
